import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { getConnections, getInvoiceProductAnalysis } from '../../api/api';

function InvoiceAnalysis() {
  const { t } = useTranslation();
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      const response = await getConnections();
      setConnections(response.data.data);
      if (response.data.data.length > 0) {
        setSelectedConnection(response.data.data[0].id);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  useEffect(() => {
    if (selectedConnection) {
      loadInvoiceAnalysis();
    }
  }, [selectedConnection]);

  // Filtrar productos basado en la búsqueda
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredProducts(products);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = products.filter(product =>
        product.product_name.toLowerCase().includes(query) ||
        product.product_id.toString().includes(query)
      );
      setFilteredProducts(filtered);
    }
  }, [products, searchQuery]);

  const loadInvoiceAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getInvoiceProductAnalysis(selectedConnection);
      setProducts(response.data.data.products || []);
      setSummary(response.data.data.summary || null);
      console.log('[InvoiceAnalysis] Datos recibidos:', response.data.data.products);
    } catch (error) {
      console.error('Error loading invoice analysis:', error);
      setError(error.response?.data?.error || 'Error al cargar análisis de facturas');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value !== 'number') {
      value = parseFloat(value) || 0;
    }
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const getMarginColor = (marginPercent) => {
    if (marginPercent < 0) return '#d32f2f'; // Red
    if (marginPercent < 10) return '#f57c00'; // Orange
    if (marginPercent < 20) return '#fbc02d'; // Yellow
    return '#388e3c'; // Green
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: 'primary.main', fontWeight: 'bold' }}>
        📊 Análisis de Facturas - Precios Reales
      </Typography>

      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Este análisis utiliza datos reales de facturas (account.move.line) para calcular precios promedio de compra y venta.
        Solo muestra productos con ambos tipos de transacciones.
      </Typography>

      {/* Connection Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Selecciona una conexión
          </Typography>
          <TextField
            fullWidth
            select
            value={selectedConnection || ''}
            onChange={(e) => setSelectedConnection(parseInt(e.target.value))}
            SelectProps={{
              native: true,
            }}
          >
            <option value="">-- Selecciona una conexión --</option>
            {connections.map((conn) => (
              <option key={conn.id} value={conn.id}>
                {conn.name}
              </option>
            ))}
          </TextField>
        </CardContent>
      </Card>

      {selectedConnection && (
        <>
          {/* Summary Cards */}
          {summary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ReceiptIcon sx={{ mr: 1 }} />
                      <Typography color="inherit" gutterBottom>
                        Productos Analizados
                      </Typography>
                    </Box>
                    <Typography variant="h4">{summary.total_products}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Con compras Y ventas
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WarningIcon sx={{ mr: 1 }} />
                      <Typography color="inherit" gutterBottom>
                        Productos con Pérdida
                      </Typography>
                    </Box>
                    <Typography variant="h4">{summary.products_with_loss}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {summary.total_products > 0 ? ((summary.products_with_loss / summary.total_products) * 100).toFixed(1) : 0}% del total
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckIcon sx={{ mr: 1 }} />
                      <Typography color="inherit" gutterBottom>
                        Margen Promedio
                      </Typography>
                    </Box>
                    <Typography variant="h4">{summary.avg_margin_percent.toFixed(1)}%</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {summary.products_with_profit} productos rentables
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ReceiptIcon sx={{ mr: 1 }} />
                      <Typography color="inherit" gutterBottom>
                        Margen Total
                      </Typography>
                    </Box>
                    <Typography variant="h5">{formatCurrency(summary.total_margin)}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {summary.total_purchase_lines} compras / {summary.total_sales_lines} ventas
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Products Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Productos con Transacciones Reales ({filteredProducts.length}{searchQuery ? ` de ${products.length}` : ''})
              </Typography>
              
              {/* Search Box */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Buscar por nombre o ID del producto..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  variant="outlined"
                  size="small"
                  inputProps={{
                    style: { fontSize: '14px' }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    }
                  }}
                />
                {searchQuery && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                    Mostrando {filteredProducts.length} de {products.length} productos
                  </Typography>
                )}
              </Box>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : products.length === 0 && !error ? (
                <Alert severity="info">
                  No hay productos con transacciones de compra Y venta
                </Alert>
              ) : filteredProducts.length === 0 && searchQuery ? (
                <Alert severity="info">
                  No se encontraron productos que coincidan con "{searchQuery}"
                </Alert>
              ) : filteredProducts.length > 0 && (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ width: 48, fontWeight: 'bold' }}>-</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Producto</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          <Tooltip title="Precio promedio real de compra según facturas">
                            <span>Precio Compra Promedio</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          <Tooltip title="Precio promedio real de venta según facturas">
                            <span>Precio Venta Promedio</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          Margen Unitario
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          Margen %
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          Cant. Comprada
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          Cant. Vendida
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">
                          Transacciones
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">
                          Estado
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredProducts.map((product) => {
                        const isExpanded = expandedProduct === product.product_id;
                        return (
                          <>
                            <TableRow key={`row-${product.product_id}`} hover sx={{ cursor: 'pointer' }}>
                              <TableCell sx={{ width: 48, padding: '8px' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => setExpandedProduct(isExpanded ? null : product.product_id)}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {product.product_name}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                  ID: {product.product_id}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: '600', color: '#d32f2f' }}>
                            {formatCurrency(product.avg_purchase_price)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: '600', color: '#388e3c' }}>
                            {formatCurrency(product.avg_sales_price)}
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: product.margin < 0 ? '#d32f2f' : '#388e3c'
                            }}
                          >
                            {formatCurrency(product.margin)}
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: getMarginColor(product.margin_percent)
                            }}
                          >
                            {product.margin_percent.toFixed(2)}%
                          </TableCell>
                          <TableCell align="right">
                            {product.purchase_qty.toFixed(2)}
                          </TableCell>                              <TableCell align="right">
                                {product.sales_qty.toFixed(2)}
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="caption">
                                  {product.purchase_transactions} compras
                                </Typography>
                                <br />
                                <Typography variant="caption">
                                  {product.sales_transactions} ventas
                                </Typography>
                              </TableCell>                              <TableCell align="center">
                                {product.has_loss ? (
                                  <Chip
                                    icon={<WarningIcon />}
                                    label="PÉRDIDA"
                                    color="error"
                                    size="small"
                                  />
                                ) : (
                                  <Chip
                                    icon={<CheckIcon />}
                                    label="GANANCIA"
                                    color="success"
                                    size="small"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow key={`expand-${product.product_id}`}>
                              <TableCell colSpan={10} sx={{ padding: 0 }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                                    <Grid container spacing={3}>
                                      {/* Información General del Producto */}
                                      <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                                          📊 Análisis Detallado de Transacciones Reales
                                        </Typography>
                                      </Grid>

                                      {/* Columna Izquierda: Datos de Compras */}
                                      <Grid item xs={12} md={6}>
                                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fff3f3' }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#d32f2f' }}>
                                            🛒 Transacciones de Compra
                                          </Typography>
                                          <Box sx={{ ml: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="body2" sx={{ opacity: 0.8 }}>Número de Transacciones:</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {product.purchase_transactions}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="body2" sx={{ opacity: 0.8 }}>Facturas Únicas:</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {product.purchase_invoices ? product.purchase_invoices.length : 0}
                                              </Typography>
                                            </Box>
                                            {product.purchase_invoices && product.purchase_invoices.length > 0 && (
                                              <Box sx={{ py: 1 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                                                  <strong>Facturas ({product.purchase_invoices.length}):</strong>
                                                </Typography>
                                                <Box sx={{ ml: 1 }}>
                                                  {product.purchase_invoices.map((invoice, idx) => (
                                                    <Box key={idx} sx={{ mb: 0.5, backgroundColor: '#ffe0e0', p: 0.75, borderRadius: 0.5 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                          • {typeof invoice === 'string' ? invoice : invoice.number}
                                                        </Typography>
                                                        {typeof invoice === 'object' && invoice.partner && (
                                                          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                                                            Proveedor: {invoice.partner}
                                                          </Typography>
                                                        )}
                                                      </Box>
                                                    </Box>
                                                  ))}
                                                </Box>
                                              </Box>
                                            )}
                                            {(!product.purchase_invoices || product.purchase_invoices.length === 0) && (
                                              <Box sx={{ py: 1 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                                                  No hay facturas disponibles
                                                </Typography>
                                              </Box>
                                            )}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="body2" sx={{ opacity: 0.8 }}>Cantidad Total Comprada:</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {product.purchase_qty.toFixed(2)} unidades
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="body2" sx={{ opacity: 0.8 }}>Precio Promedio Unitario:</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                                                {formatCurrency(product.avg_purchase_price)}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderTop: '1px solid #ffcdd2', mt: 0.5, pt: 1 }}>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>Coste Total de Compras:</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                                                {formatCurrency(product.avg_purchase_price * product.purchase_qty)}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Box>
                                      </Grid>

                                      {/* Columna Derecha: Datos de Ventas */}
                                      <Grid item xs={12} md={6}>
                                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#f1f8f4' }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#388e3c' }}>
                                            💰 Transacciones de Venta
                                          </Typography>
                                          <Box sx={{ ml: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="body2" sx={{ opacity: 0.8 }}>Número de Transacciones:</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {product.sales_transactions}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="body2" sx={{ opacity: 0.8 }}>Facturas Únicas:</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {product.sales_invoices ? product.sales_invoices.length : 0}
                                              </Typography>
                                            </Box>
                                            {product.sales_invoices && product.sales_invoices.length > 0 && (
                                              <Box sx={{ py: 1 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                                                  <strong>Facturas ({product.sales_invoices.length}):</strong>
                                                </Typography>
                                                <Box sx={{ ml: 1 }}>
                                                  {product.sales_invoices.map((invoice, idx) => (
                                                    <Box key={idx} sx={{ mb: 0.5, backgroundColor: '#c8e6c9', p: 0.75, borderRadius: 0.5 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                          • {typeof invoice === 'string' ? invoice : invoice.number}
                                                        </Typography>
                                                        {typeof invoice === 'object' && invoice.partner && (
                                                          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                                                            Cliente: {invoice.partner}
                                                          </Typography>
                                                        )}
                                                      </Box>
                                                    </Box>
                                                  ))}
                                                </Box>
                                              </Box>
                                            )}
                                            {(!product.sales_invoices || product.sales_invoices.length === 0) && (
                                              <Box sx={{ py: 1 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                                                  No hay facturas disponibles
                                                </Typography>
                                              </Box>
                                            )}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="body2" sx={{ opacity: 0.8 }}>Cantidad Total Vendida:</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {product.sales_qty.toFixed(2)} unidades
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="body2" sx={{ opacity: 0.8 }}>Precio Promedio Unitario:</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#388e3c' }}>
                                                {formatCurrency(product.avg_sales_price)}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderTop: '1px solid #c8e6c9', mt: 0.5, pt: 1 }}>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>Ingresos Totales de Ventas:</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#388e3c' }}>
                                                {formatCurrency(product.avg_sales_price * product.sales_qty)}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Box>
                                      </Grid>

                                      {/* Fila inferior: Análisis de Rentabilidad */}
                                      <Grid item xs={12}>
                                        <Box sx={{ p: 2, border: '2px solid', borderColor: product.has_loss ? '#d32f2f' : '#388e3c', borderRadius: 2, backgroundColor: product.has_loss ? '#ffebee' : '#e8f5e9' }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: product.has_loss ? '#d32f2f' : '#388e3c' }}>
                                            {product.has_loss ? '⚠️ Análisis de Pérdida' : '✓ Análisis de Rentabilidad'}
                                          </Typography>
                                          <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={3}>
                                              <Box sx={{ textAlign: 'center', p: 1 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                                                  Margen Unitario
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: product.margin < 0 ? '#d32f2f' : '#388e3c' }}>
                                                  {formatCurrency(product.margin)}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                              <Box sx={{ textAlign: 'center', p: 1 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                                                  Margen %
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: getMarginColor(product.margin_percent) }}>
                                                  {product.margin_percent.toFixed(2)}%
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                              <Box sx={{ textAlign: 'center', p: 1 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                                                  Margen Total (Vendidas)
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: product.margin < 0 ? '#d32f2f' : '#388e3c' }}>
                                                  {formatCurrency(product.margin * product.sales_qty)}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                              <Box sx={{ textAlign: 'center', p: 1 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                                                  Stock/Diferencia
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                  {(product.purchase_qty - product.sales_qty).toFixed(2)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                                  unidades
                                                </Typography>
                                              </Box>
                                            </Grid>
                                          </Grid>
                                          {product.has_loss && (
                                            <Alert severity="error" sx={{ mt: 2 }}>
                                              <Typography variant="body2">
                                                <strong>¡Atención!</strong> Este producto está generando pérdidas. El precio de venta promedio ({formatCurrency(product.avg_sales_price)}) 
                                                es inferior al precio de compra promedio ({formatCurrency(product.avg_purchase_price)}). 
                                                Considera revisar la estrategia de precios.
                                              </Typography>
                                            </Alert>
                                          )}
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

export default InvoiceAnalysis;
